import { Component, forwardRef, inject, Input } from '@angular/core';
import { PaymentTypeOption } from '../../models/payment-type-option.interface';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { PaymentType } from '../../models/payment-type.type';
import { ControlsOf } from '@shared/models-v3/controls-of.type';
import { NgClass } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SkeletonModule } from 'primeng/skeleton';

const NG_MODULES = [ReactiveFormsModule, NgClass];
const PRIME_MODULES = [ButtonModule, RadioButtonModule, SkeletonModule];

@Component({
  selector: 'app-payment-method-form',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES],
  templateUrl: './payment-method-form.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PaymentMethodFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PaymentMethodFormComponent),
      multi: true,
    },
  ],
})
export class PaymentMethodFormComponent
  implements ControlValueAccessor, Validator
{
  @Input({ required: true }) payments: PaymentTypeOption[] = [];
  @Input({ required: true }) isLoading: boolean = true;

  private onChange: (value: string) => void = () => {};
  private onTouch: () => void = () => {};

  get paymentTypeField(): FormControl<string> {
    return this.form.controls.paymentType;
  }

  form!: FormGroup<ControlsOf<{ paymentType: PaymentType }>>;
  private readonly fb = inject(FormBuilder);

  constructor() {
    this.buildForm();
  }

  private buildForm(): void {
    this.form = this.fb.group({
      paymentType: new FormControl<PaymentType>('' as PaymentType, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
  }

  /**
   * Validar el control síncronamente.
   * @param control The control to validate.
   * @returns The validation errors or null.
   * */
  validate(): ValidationErrors | null {
    return this.paymentTypeField.errors;
  }

  onSelectPaymentType(value: PaymentType): void {
    this.paymentTypeField.setValue(value);
    this.onChange(value);
    this.onTouch();
  }

  writeValue(value: string): void {
    if (!value) return;
    this.paymentTypeField.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }
}
